import React, { useState } from 'react';
import { GridColDef } from '@mui/x-data-grid';
import { Box, Tooltip, Typography } from '@mui/material';
import MuiDataTable from 'components/mui-data-table/mui-data-table';
import { useUserSessionsQuery } from 'app/store';
import osExtractor from 'features/profile/components/user-agent/utils/os-extractor/os-extractor';
import { translator } from '../user-agent/utils/translator/translator';
import browserExtractor from 'features/profile/components/user-agent/utils/browser-extractor/browser-extractor';
import { noRowsTitle, timeConverter } from 'utils/utils';
import Navigator from 'components/navigator/navigator';
import { RevokeSessionDialogue } from './revoke-session-dialogue';

export function SessionsDatatable() {
    const [page, setPage] = useState(1);

    const {
        data,
        isFetching: isLoadingData,
        isSuccess,
        refetch,
    } = useUserSessionsQuery({
        page: page,
        perPage: 15,
    });

    const rowsData: any[] = data?.items ?? [];
    const paginateInfo = data?.pagination;

    const handleIcon = (category: string, iconName: string): string => {
        return `/assets/${category}/${iconName}.svg`;
    };

    const getCountryLabel = (location: any) =>
        location?.country ||
        location?.country_name ||
        location?.name ||
        location?.iso_code ||
        'کشور نامشخص';

    const columns: GridColDef[] = [
        {
            field: 'order',
            headerName: '#',
            minWidth: 50,
            width: 50,
            renderCell: ({ row }) =>
                rowsData.findIndex((item) => item?.id === row.id) + 1,
        },
        {
            field: 'browser',
            headerName: 'مرورگر',
            minWidth: 82,
            align: 'center',
            headerAlign: 'center',
            renderCell: ({ row }) => {
                const browser = browserExtractor(row.user_agent)?.name || 'other';
                const label = translator(browser);

                return (
                    <Tooltip title={label} arrow placement="top">
                        <Box
                            component="span"
                            sx={{
                                width: 34,
                                height: 34,
                                display: 'grid',
                                placeItems: 'center',
                                borderRadius: 1.5,
                                cursor: 'default',
                                '&:hover': { bgcolor: 'action.hover' },
                            }}
                        >
                            {browser !== 'other' ? (
                                <img
                                    src={handleIcon('browsers', browser)}
                                    onError={(e: React.SyntheticEvent<HTMLImageElement, Event>) => {
                                        e.currentTarget.style.display = 'none';
                                    }}
                                    alt={label}
                                    width="25"
                                    height="25"
                                />
                            ) : (
                                <i
                                    className="fa-light fa-globe"
                                    style={{ fontSize: '17px' }}
                                />
                            )}
                        </Box>
                    </Tooltip>
                );
            },
        },
        {
            field: 'os',
            headerName: 'سیستم عامل',
            minWidth: 92,
            align: 'center',
            headerAlign: 'center',
            renderCell: ({ row }) => {
                const os = osExtractor(row.user_agent)?.name || 'other';
                const label = translator(os);

                return (
                    <Tooltip title={label} arrow placement="top">
                        <Box
                            component="span"
                            sx={{
                                width: 34,
                                height: 34,
                                display: 'grid',
                                placeItems: 'center',
                                borderRadius: 1.5,
                                cursor: 'default',
                                '&:hover': { bgcolor: 'action.hover' },
                            }}
                        >
                            {os !== 'other' ? (
                                <img
                                    src={handleIcon('os', os)}
                                    onError={(e: React.SyntheticEvent<HTMLImageElement, Event>) => {
                                        e.currentTarget.style.display = 'none';
                                    }}
                                    alt={label}
                                    width="21"
                                    height="21"
                                />
                            ) : (
                                <i
                                    className="fa-light fa-square-terminal"
                                    style={{ fontSize: '18px' }}
                                />
                            )}
                        </Box>
                    </Tooltip>
                );
            },
        },
        {
            field: 'ip_address',
            headerName: 'آی پی',
            minWidth: 165,
            cellClassName: 'ltr-cell',
            renderCell: ({ row }) => (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    <Tooltip title={translator(getCountryLabel(row.location))} arrow placement="top">
                        <Box
                            component="span"
                            sx={{
                                width: 28,
                                height: 28,
                                display: 'grid',
                                placeItems: 'center',
                                borderRadius: 1.25,
                                cursor: 'default',
                                '&:hover': { bgcolor: 'action.hover' },
                            }}
                        >
                            {row.location?.iso_code ? (
                                <img
                                    src={handleIcon('flags', row.location.iso_code)}
                                    onError={(e: React.SyntheticEvent<HTMLImageElement, Event>) => {
                                        e.currentTarget.style.display = 'none';
                                    }}
                                    alt={getCountryLabel(row.location)}
                                    width="20"
                                    height="20"
                                />
                            ) : (
                                <i
                                    className="fa-light fa-earth-americas"
                                    style={{ fontSize: '17px' }}
                                />
                            )}
                        </Box>
                    </Tooltip>
                    <Navigator
                        label={row.ip_address}
                        url={`https://whois.domaintools.com/${row.ip_address}`}
                    />
                </Box>
            ),
        },
        {
            field: 'login_at',
            headerName: 'زمان ورود',
            minWidth: 170,
            renderCell: ({ row }) =>
                row.login_at ? timeConverter(row.login_at) : '-',
        },
        {
            field: 'last_activity_at',
            headerName: 'آخرین فعالیت',
            minWidth: 180,
            renderCell: ({ row }) => {
                const value = row.last_activity_at || row.updated_at;
                return value ? timeConverter(value) : '-';
            },
        },
        {
            field: 'revoke',
            headerName: '',
            minWidth: 120,
            renderCell: ({ row }: any) => {
                return row.is_current ? (
                    <Typography
                        variant="body1"
                        color="error.light"
                        fontSize={14}
                        sx={{ padding: '6px 8px' }}
                    >
                        نشست فعلی
                    </Typography>
                ) : (
                    <RevokeSessionDialogue
                        id={row.token_id}
                        ipAddress={row.ip_address}
                        onDeleted={refetch}
                    />
                );
            },
        },
    ];

    return (
        <MuiDataTable
            rows={rowsData}
            columns={columns}
            isLoading={isLoadingData}
            isSuccess={isSuccess}
            onRefetch={refetch}
            paginateInfo={paginateInfo}
            setPage={setPage}
            noRowsMessage={noRowsTitle('نشست فعالی')}
            headerStyle="dark"
        />
    );
}
